/**
 * @fileoverview Theme metadata hook for device integration
 * Manages theme-color meta tag updates for browser and device theming
 * Provides seamless integration with device UI elements like status bars
 */

import { useEffect } from 'react';
import { useTheme } from './use-theme';

/**
 * Theme color mapping for different theme variants
 * Colors are used for browser and device UI integration
 * Each theme has a specific color to match its visual style
 */
const themeColors = {
  dark: '#0A0A0A',        // Dark theme background
  light: '#FFFFFF',       // Light theme background
  autumn: '#8B4513',      // Warm autumn theme
  winter: '#1E90FF',      // Cool winter theme
  minimal: '#1C1C1C'      // Clean minimal theme
};

/**
 * Hook to manage theme metadata for browser and device integration
 * Automatically updates theme-color meta tag when theme changes
 * This affects browser UI elements and mobile device status bars
 */
export function useThemeMeta() {
  const { theme } = useTheme();

  useEffect(() => {
    // Remove any existing theme-color meta tag to prevent duplicates
    const existingMeta = document.querySelector('meta[name="theme-color"]');
    if (existingMeta) {
      existingMeta.remove();
    }

    // Create and append new meta tag with current theme color
    const metaThemeColor = document.createElement('meta');
    metaThemeColor.setAttribute('name', 'theme-color');
    metaThemeColor.setAttribute('content', themeColors[theme]);
    document.head.appendChild(metaThemeColor);

    // Cleanup function to remove meta tag on unmount
    return () => {
      metaThemeColor.remove();
    };
  }, [theme]);
}